import { db } from '../config/firebase';
import { collection, addDoc, updateDoc, doc, getDocs, getDoc, query, where, orderBy, serverTimestamp, deleteDoc, enableNetwork, disableNetwork } from 'firebase/firestore';
import walletService from './walletService';

class BookingService {
  // Create a new booking
  async createBooking(bookingData) {
    try {
      // Ensure proper field mapping for database consistency
      const booking = {
        // Client information - only store ID, fetch profile data when needed
        clientId: bookingData.userId || bookingData.user_id || bookingData.clientId,
        
        // Lawyer information - only store ID, fetch profile data when needed
        lawyerId: bookingData.lawyerId || bookingData.lawyer_id,
        
        // Booking details
        appointmentDate: bookingData.appointmentDate || bookingData.date || bookingData.appointment_date,
        appointmentTime: bookingData.appointmentTime || bookingData.time || bookingData.appointment_time,
        consultationType: bookingData.consultationType || bookingData.consultation_type || 'general',
        caseType: bookingData.caseType || bookingData.consultationType || 'Legal Consultation',
        description: bookingData.description || '',
        totalAmount: bookingData.totalAmount || bookingData.amount || 0,
        consultationFees: bookingData.consultationFees || 0,
        serviceCharge: bookingData.serviceCharge || 200,
        
        // Status and metadata
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lawyerAccepted: false,
        lawyerRejected: false,
        consultationCompleted: false,
        paymentStatus: 'paid',
        refundStatus: 'none',
        isEmergency: bookingData.isEmergency || false
      };

      const docRef = await addDoc(collection(db, 'bookings'), booking);
      
      // Update the booking with its ID
      await updateDoc(docRef, { bookingId: docRef.id });
      
      return {
        success: true,
        bookingId: docRef.id,
        data: { ...booking, bookingId: docRef.id }
      };
    } catch (error) {
      console.error('Error creating booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get all bookings for admin dashboard
  async getAllBookings() {
    try {
      const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const bookings = [];
      querySnapshot.forEach((doc) => {
        bookings.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: bookings
      };
    } catch (error) {
      console.error('Error getting all bookings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get lawyer's pending cases
  async getLawyerPendingCases(lawyerId) {
    try {
      // Force fresh data by temporarily disabling and re-enabling network
      await disableNetwork(db);
      await enableNetwork(db);
      
      const q = query(
        collection(db, 'bookings'),
        where('lawyerId', '==', lawyerId),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const pendingCases = [];
      querySnapshot.forEach((doc) => {
        pendingCases.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: pendingCases
      };
    } catch (error) {
      console.error('Error getting lawyer pending cases:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get lawyer's active cases
  async getLawyerActiveCases(lawyerId) {
    try {
      // Force fresh data by temporarily disabling and re-enabling network
      await disableNetwork(db);
      await enableNetwork(db);
      
      const q = query(
        collection(db, 'bookings'),
        where('lawyerId', '==', lawyerId),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const activeCases = [];
      querySnapshot.forEach((doc) => {
        activeCases.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: activeCases
      };
    } catch (error) {
      console.error('Error getting lawyer active cases:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get lawyer's completed cases
  async getLawyerCompletedCases(lawyerId) {
    try {
      // Force fresh data by temporarily disabling and re-enabling network
      await disableNetwork(db);
      await enableNetwork(db);
      
      const q = query(
        collection(db, 'bookings'),
        where('lawyerId', '==', lawyerId),
        where('status', '==', 'completed'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const completedCases = [];
      querySnapshot.forEach((doc) => {
        completedCases.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: completedCases
      };
    } catch (error) {
      console.error('Error getting lawyer completed cases:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get client's bookings
  async getClientBookings(clientId) {
    try {
      const q = query(
        collection(db, 'bookings'),
        where('userId', '==', clientId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const clientBookings = [];
      
              // Process each booking and fetch lawyer details
        for (const docSnapshot of querySnapshot.docs) {
          const bookingData = { id: docSnapshot.id, ...docSnapshot.data() };
          console.log('📋 Raw booking data:', bookingData);
          
          // Check if booking has been rated
          if (bookingData.hasRated) {
            console.log('✅ Booking has been rated:', bookingData.clientRating, '/5');
          }
        
        // Fetch lawyer details if lawyerId exists
        if (bookingData.lawyerId) {
          try {
            const lawyerDoc = await getDoc(doc(db, 'lawyers', bookingData.lawyerId));
            if (lawyerDoc.exists()) {
              const lawyerData = lawyerDoc.data();
              console.log('📋 Lawyer data found for booking:', bookingData.id, lawyerData);
              
              // Enhance booking data with lawyer details
              bookingData.lawyerName = lawyerData.full_name || lawyerData.displayName || lawyerData.name || bookingData.lawyerName || 'Lawyer';
              bookingData.specialization = lawyerData.specialization?.join(', ') || lawyerData.specialization || 'General Law';
              bookingData.location = lawyerData.city || lawyerData.location || lawyerData.bar_council_state || 'Location not specified';
              bookingData.rating = lawyerData.average_rating || lawyerData.rating || 0;
              bookingData.experience = lawyerData.experience_years || lawyerData.experience || 0;
              bookingData.profilePic = lawyerData.profile_picture || lawyerData.profilePic || lawyerData.photoURL || lawyerData.profile_pic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face';
              bookingData.lawyerEmail = lawyerData.email || bookingData.lawyerEmail || '';
              
              // Add BAR License information
              bookingData.barLicense = lawyerData.bar_council_number || lawyerData.barCouncilLicense || lawyerData.barLicense || lawyerData.license_number || 'Not specified';
              
              // Add Chamber Address
              bookingData.chamberAddress = lawyerData.chamber_address || lawyerData.chamberAddress || lawyerData.address || lawyerData.office_address || 'Not specified';
              
              // Add Consultation Fees
              bookingData.consultationFees = lawyerData.fees?.consultation || lawyerData.consultation_fee || lawyerData.consultationFee || lawyerData.fee || bookingData.consultationFees || 2500;
              
              // Add other fee types
              bookingData.textFee = lawyerData.fees?.text || lawyerData.text_fee || 1500;
              bookingData.callFee = lawyerData.fees?.call || lawyerData.call_fee || 2500;
              bookingData.videoFee = lawyerData.fees?.video || lawyerData.video_fee || 3000;
              bookingData.inPersonFee = lawyerData.fees?.in_person || lawyerData.in_person_fee || 3500;
              bookingData.emergencyFee = lawyerData.fees?.emergency || lawyerData.emergency_fee || 5000;
              
              // Fix consultation type mapping - use the actual booking consultation type
              if (bookingData.consultationType === 'general') {
                // Try to get the actual consultation type from the booking
                bookingData.consultationType = bookingData.type || bookingData.consultation_type || 'Video Call';
              }
              
              // Also set the type field for compatibility
              bookingData.type = bookingData.consultationType;
              
              // Add lawyer object for compatibility
              bookingData.lawyer = {
                id: bookingData.lawyerId,
                name: bookingData.lawyerName,
                specialization: bookingData.specialization,
                location: bookingData.location,
                rating: bookingData.rating,
                experience: bookingData.experience,
                profilePic: bookingData.profilePic,
                email: bookingData.lawyerEmail,
                barLicense: bookingData.barLicense,
                chamberAddress: bookingData.chamberAddress,
                consultationFees: bookingData.consultationFees,
                fees: {
                  text: bookingData.textFee,
                  call: bookingData.callFee,
                  video: bookingData.videoFee,
                  in_person: bookingData.inPersonFee,
                  emergency: bookingData.emergencyFee,
                  consultation: bookingData.consultationFees
                }
              };
              
              console.log('✅ Enhanced booking data:', {
                lawyerName: bookingData.lawyerName,
                specialization: bookingData.specialization,
                location: bookingData.location,
                rating: bookingData.rating,
                experience: bookingData.experience,
                profilePic: bookingData.profilePic
              });
            }
          } catch (lawyerError) {
            console.error('Error fetching lawyer details for booking:', bookingData.id, lawyerError);
            // Keep the booking data even if lawyer details fail to load
            // Set default values for missing lawyer details
            bookingData.lawyerName = bookingData.lawyerName || 'Lawyer';
            bookingData.specialization = bookingData.specialization || 'General Law';
            bookingData.location = bookingData.location || 'Location not specified';
            bookingData.rating = bookingData.rating || 0;
            bookingData.experience = bookingData.experience || 0;
            bookingData.profilePic = bookingData.profilePic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face';
            bookingData.barLicense = bookingData.barLicense || 'Not specified';
            bookingData.chamberAddress = bookingData.chamberAddress || 'Not specified';
            bookingData.consultationFees = bookingData.consultationFees || 2500;
          }
        }
        
        clientBookings.push(bookingData);
      }
      
      return {
        success: true,
        data: clientBookings
      };
    } catch (error) {
      console.error('Error getting client bookings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Lawyer accepts booking
  async acceptBooking(bookingId) {
    try {
      console.log('🔄 Accepting booking:', bookingId);
      
      // First, get the booking data to create chat
      const bookingRef = doc(db, 'bookings', bookingId);
      const bookingDoc = await getDoc(bookingRef);
      
      if (!bookingDoc.exists()) {
        console.error('❌ Booking not found:', bookingId);
        return { success: false, error: 'Booking not found' };
      }
      
      const bookingData = { id: bookingDoc.id, ...bookingDoc.data() };
      console.log('📋 Booking data found:', bookingData);
      
      // Update booking status
      await updateDoc(bookingRef, {
        status: 'active',
        lawyerAccepted: true,
        lawyerAcceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Create chat room for this booking if booking data exists
      if (bookingData) {
        // Fetch real profile data for client and lawyer
        let clientProfile = null;
        let lawyerProfile = null;
        
        try {
          // Get the correct clientId first
          const clientId = bookingData.userId || bookingData.clientId || bookingData.user_id || bookingData.client_id;
          console.log('🔍 Fetching client profile for clientId:', clientId);
          
          if (clientId) {
            // Fetch client profile from users collection
            const clientDoc = await getDoc(doc(db, 'users', clientId));
            if (clientDoc.exists()) {
              clientProfile = clientDoc.data();
              console.log('✅ Client profile fetched:', clientProfile);
            } else {
              console.log('❌ Client document not found for ID:', clientId);
            }
          } else {
            console.log('❌ No clientId available for profile fetching');
          }
          
          // Fetch lawyer profile from lawyers collection
          const lawyerDoc = await getDoc(doc(db, 'lawyers', bookingData.lawyerId));
          if (lawyerDoc.exists()) {
            lawyerProfile = lawyerDoc.data();
            console.log('✅ Lawyer profile fetched:', lawyerProfile);
          }
        } catch (error) {
          console.error('❌ Error fetching profiles:', error);
        }
        
        // Extract client information with real profile data - try multiple field names
        const clientName = clientProfile?.full_name || 
                          clientProfile?.fullName || 
                          clientProfile?.displayName || 
                          clientProfile?.name || 
                          (clientProfile?.firstName && clientProfile?.lastName ? `${clientProfile.firstName} ${clientProfile.lastName}` : null) ||
                          bookingData.clientName || 
                          bookingData.userName || 
                          'Client';
        
        const clientEmail = clientProfile?.email || 
                           bookingData.userEmail || 
                           bookingData.clientEmail || 
                           'No email';
        
        const clientPhoto = clientProfile?.profile_picture || 
                           clientProfile?.profilePic || 
                           clientProfile?.photoURL || 
                           clientProfile?.avatar || 
                           clientProfile?.photo || 
                           bookingData.clientPhoto || 
                           'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face';
        
        console.log('🔍 Client profile data:', {
          clientName,
          clientEmail,
          clientPhoto,
          rawClientProfile: clientProfile
        });
        
        // Extract lawyer information with real profile data
        const lawyerName = lawyerProfile?.full_name || lawyerProfile?.displayName || lawyerProfile?.name || 
                          bookingData.lawyerName || 'Lawyer';
        const lawyerEmail = lawyerProfile?.email || bookingData.lawyerEmail || 'No email';
        const lawyerPhoto = lawyerProfile?.profile_picture || lawyerProfile?.profilePic || lawyerProfile?.photoURL || 
                           bookingData.lawyerPhoto || '/default-lawyer.png';
        const lawyerProfilePic = lawyerProfile?.profile_picture || lawyerProfile?.profilePic || lawyerProfile?.photoURL || 
                                bookingData.lawyerPhoto || '/default-lawyer.png';
        const specialization = lawyerProfile?.specialization?.join(', ') || lawyerProfile?.specialization || 
                              bookingData.specialization || 'General Law';
        
        console.log('📋 Final chat data:', {
          clientName,
          clientEmail,
          clientPhoto,
          lawyerName,
          lawyerEmail,
          lawyerPhoto,
          lawyerProfilePic,
          specialization
        });
        
        // Ensure we have a valid clientId - try multiple field names
        const clientId = bookingData.userId || bookingData.clientId || bookingData.user_id || bookingData.client_id;
        
        console.log('🔍 Raw booking data for clientId extraction:', {
          userId: bookingData.userId,
          clientId: bookingData.clientId,
          user_id: bookingData.user_id,
          client_id: bookingData.client_id,
          allKeys: Object.keys(bookingData)
        });
        
        if (!clientId) {
          console.error('❌ No clientId found in booking data:', bookingData);
          console.error('❌ Available fields:', Object.keys(bookingData));
          return {
            success: false,
            error: 'Client ID not found in booking data'
          };
        }
        
        console.log('✅ Using clientId:', clientId, 'from booking data');
        
        const chatData = {
          bookingId: bookingId,
          lawyerId: bookingData.lawyerId,
          clientId: clientId,
          lawyerName: lawyerName,
          clientName: clientName,
          lawyerPhoto: lawyerPhoto,
          lawyerProfilePic: lawyerProfilePic,
          clientPhoto: clientPhoto,
          clientProfilePic: clientPhoto, // Add this field for consistency
          lawyerEmail: lawyerEmail,
          clientEmail: clientEmail,
          specialization: specialization,
          caseType: bookingData.caseType || bookingData.consultationType || 'Legal Consultation',
          consultationMode: bookingData.consultationType || 'Text',
          consultationDate: bookingData.appointmentDate || bookingData.date || new Date().toISOString().split('T')[0],
          consultationTime: bookingData.appointmentTime || bookingData.time || '12:00 PM',
          consultationTypes: [bookingData.consultationType || 'text'],
          participants: [clientId, bookingData.lawyerId],
          status: 'active', // This should be 'active' not 'pending'
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastMessage: `New consultation started`, // Simple message for lawyer view
          lastMessageTime: serverTimestamp(),
          unreadCount: 0,
          // Add booking time for reference
          bookingDate: new Date().toISOString(),
          lastBookingTime: serverTimestamp()
        };

        // Import chatService dynamically to avoid circular dependency
        const { chatService } = await import('./chatService.js');
        console.log('🔄 Creating chat with data:', chatData);
        const chatResult = await chatService.createChat(chatData);

        if (chatResult.success) {
          console.log('✅ Chat room created successfully:', chatResult.chatId);
          console.log('✅ Chat result:', chatResult);
          
          // Update booking with chat ID
          await updateDoc(bookingRef, {
            chatId: chatResult.chatId
          });
          console.log('✅ Booking updated with chat ID:', chatResult.chatId);
          
          // Also update any existing pending chats to active status
          try {
            const { collection, query, where, getDocs, updateDoc: updateDocChat } = await import('firebase/firestore');
            const existingChatsQuery = query(
              collection(db, 'chats'),
              where('lawyerId', '==', bookingData.lawyerId),
              where('clientId', '==', clientId),
              where('status', '==', 'pending')
            );
            
            const existingChatsSnapshot = await getDocs(existingChatsQuery);
            if (!existingChatsSnapshot.empty) {
              console.log('🔄 Updating existing pending chats to active status...');
              const updatePromises = existingChatsSnapshot.docs.map(doc => 
                updateDocChat(doc.ref, { 
                  status: 'active',
                  updatedAt: serverTimestamp()
                })
              );
              await Promise.all(updatePromises);
              console.log('✅ Updated', existingChatsSnapshot.size, 'pending chats to active');
            }
          } catch (updateError) {
            console.error('❌ Error updating existing chats:', updateError);
          }
        } else {
          console.error('❌ Failed to create chat room:', chatResult.error);
          console.error('❌ Chat result:', chatResult);
        }

        // Add pending amount to lawyer's wallet (after 20% commission)
        const walletResult = await walletService.addPendingAmount(
          bookingData.lawyerId,
          bookingId,
          bookingData.totalAmount || bookingData.amount,
          {
            clientName: clientName,
            caseType: bookingData.caseType || bookingData.consultationType || 'Legal Consultation',
            consultationMode: bookingData.consultationType || 'Text',
            bookingDate: bookingData.appointmentDate || bookingData.date || new Date().toISOString()
          }
        );

        if (walletResult.success) {
          console.log('✅ Pending amount added to lawyer wallet:', {
            netAmount: walletResult.netAmount,
            commission: walletResult.commission,
            totalAmount: bookingData.totalAmount || bookingData.amount
          });
        } else {
          console.error('❌ Failed to add pending amount to wallet:', walletResult.error);
        }
      }

      return {
        success: true,
        message: 'Booking accepted successfully',
        chatCreated: !!bookingData
      };
    } catch (error) {
      console.error('Error accepting booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Lawyer rejects booking
  async rejectBooking(bookingId, reason = '') {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status: 'rejected',
        lawyerRejected: true,
        lawyerRejectedAt: serverTimestamp(),
        rejectionReason: reason,
        updatedAt: serverTimestamp(),
        refundStatus: 'pending'
      });

      return {
        success: true,
        message: 'Booking rejected successfully'
      };
    } catch (error) {
      console.error('Error rejecting booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Complete consultation
  async completeConsultation(bookingId) {
    try {
      console.log('🔄 Completing consultation:', bookingId);
      
      // First, get the booking data to find the lawyer
      const bookingRef = doc(db, 'bookings', bookingId);
      const bookingDoc = await getDoc(bookingRef);
      
      if (!bookingDoc.exists()) {
        console.error('❌ Booking not found:', bookingId);
        return { success: false, error: 'Booking not found' };
      }
      
      const bookingData = { id: bookingDoc.id, ...bookingDoc.data() };
      console.log('📋 Booking data found:', bookingData);

      // Update booking status
      await updateDoc(bookingRef, {
        status: 'completed',
        consultationCompleted: true,
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Settle amount in lawyer's wallet if booking data exists
      if (bookingData && bookingData.lawyerId) {
        const settlementResult = await walletService.settleAmount(bookingData.lawyerId, bookingId);
        
        if (settlementResult.success) {
          console.log('✅ Amount settled in lawyer wallet:', {
            settlementAmount: settlementResult.settlementAmount,
            newAvailableBalance: settlementResult.newAvailableBalance
          });
        } else {
          console.error('❌ Failed to settle amount:', settlementResult.error);
        }
      }

      return {
        success: true,
        message: 'Consultation completed successfully',
        amountSettled: bookingData ? true : false
      };
    } catch (error) {
      console.error('Error completing consultation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update lawyer wallet
  async updateLawyerWallet(lawyerId, amount) {
    try {
      // This would typically update the lawyer's wallet in the database
      // For now, we'll use localStorage as a placeholder
      const existingWallet = JSON.parse(localStorage.getItem(`lawyer_wallet_${lawyerId}`) || '0');
      const newBalance = existingWallet + amount;
      localStorage.setItem(`lawyer_wallet_${lawyerId}`, JSON.stringify(newBalance));

      return {
        success: true,
        newBalance
      };
    } catch (error) {
      console.error('Error updating lawyer wallet:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Refund client
  async refundClient(clientId, amount) {
    try {
      // This would typically update the client's wallet in the database
      // For now, we'll use localStorage as a placeholder
      const existingWallet = JSON.parse(localStorage.getItem(`client_wallet_${clientId}`) || '0');
      const newBalance = existingWallet + amount;
      localStorage.setItem(`client_wallet_${clientId}`, JSON.stringify(newBalance));

      return {
        success: true,
        newBalance
      };
    } catch (error) {
      console.error('Error refunding client:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete completed cases older than 24 hours
  async deleteOldCompletedCases() {
    try {
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
      
      const q = query(
        collection(db, 'bookings'),
        where('status', '==', 'completed'),
        where('completedAt', '<', twentyFourHoursAgo)
      );
      
      const querySnapshot = await getDocs(q);
      const deletePromises = [];
      
      querySnapshot.forEach((doc) => {
        deletePromises.push(deleteDoc(doc.ref));
      });
      
      await Promise.all(deletePromises);
      
      console.log(`🗑️ Deleted ${deletePromises.length} completed cases older than 24 hours`);
      
      return {
        success: true,
        deletedCount: deletePromises.length
      };
    } catch (error) {
      console.error('Error deleting old completed cases:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Cancel booking (client cancels their booking)
  async cancelBooking(bookingId, clientId, reason = '') {
    try {
      console.log('🔄 Cancelling booking:', bookingId, 'by client:', clientId);
      
      // Get the booking data first
      const bookingRef = doc(db, 'bookings', bookingId);
      const bookingDoc = await getDoc(bookingRef);
      
      if (!bookingDoc.exists()) {
        return {
          success: false,
          error: 'Booking not found'
        };
      }
      
      const bookingData = bookingDoc.data();
      
      // Verify the client owns this booking
      if (bookingData.userId !== clientId && bookingData.clientId !== clientId) {
        return {
          success: false,
          error: 'You can only cancel your own bookings'
        };
      }
      
      // Update booking status to cancelled
      await updateDoc(bookingRef, {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        cancelledBy: clientId,
        cancellationReason: reason,
        updatedAt: serverTimestamp(),
        refundStatus: 'pending'
      });
      
      // If there's an associated chat, mark it as cancelled
      if (bookingData.chatId) {
        try {
          const chatRef = doc(db, 'chats', bookingData.chatId);
          await updateDoc(chatRef, {
            status: 'cancelled',
            updatedAt: serverTimestamp()
          });
          console.log('✅ Associated chat marked as cancelled');
        } catch (chatError) {
          console.error('❌ Error updating chat status:', chatError);
        }
      }
      
      // Process refund if payment was made
      if (bookingData.paymentStatus === 'paid' && bookingData.totalAmount) {
        try {
          const refundResult = await this.refundClient(clientId, bookingData.totalAmount);
          if (refundResult.success) {
            console.log('✅ Refund processed successfully');
          } else {
            console.error('❌ Refund failed:', refundResult.error);
          }
        } catch (refundError) {
          console.error('❌ Error processing refund:', refundError);
        }
      }
      
      console.log('✅ Booking cancelled successfully');
      return {
        success: true,
        message: 'Booking cancelled successfully',
        data: {
          ...bookingData,
          status: 'cancelled',
          cancelledAt: new Date(),
          cancelledBy: clientId,
          cancellationReason: reason
        }
      };
    } catch (error) {
      console.error('Error cancelling booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete booking
  async deleteBooking(bookingId) {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await deleteDoc(bookingRef);

      return {
        success: true,
        message: 'Booking deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete multiple bookings
  async deleteMultipleBookings(bookingIds) {
    try {
      const deletePromises = bookingIds.map(bookingId => {
        const bookingRef = doc(db, 'bookings', bookingId);
        return deleteDoc(bookingRef);
      });

      await Promise.all(deletePromises);

      return {
        success: true,
        message: `${bookingIds.length} bookings deleted successfully`
      };
    } catch (error) {
      console.error('Error deleting multiple bookings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete all bookings (admin function)
  async deleteAllBookings() {
    try {
      console.log('🗑️ Deleting ALL bookings...');
      
      // Get all bookings
      const bookingsQuery = query(collection(db, 'bookings'));
      const bookingsSnapshot = await getDocs(bookingsQuery);
      
      console.log('🗑️ Found', bookingsSnapshot.size, 'bookings to delete');
      
      // Delete all bookings
      const deletePromises = bookingsSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      await Promise.all(deletePromises);
      
      console.log('🗑️ All bookings deleted successfully');
      return { 
        success: true, 
        message: `Deleted ${bookingsSnapshot.size} bookings successfully` 
      };
    } catch (error) {
      console.error('Error deleting all bookings:', error);
      return { success: false, error: error.message };
    }
  }
}

export const bookingService = new BookingService(); 